import { useCallback, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  HelpAnchor,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePagePanel,
  SurfacePageTabs,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskAssistant } from '@/versions/v1/shared/AskAssistant';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import { defaultRollupLensForPersona } from '@/versions/v1/shared/rollup-lens';
import { usePersona } from '@/app/providers';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import type { AccountingPeriod } from '@/versions/v1/schema/metal-accounting';
import { applySamplePhase, applyUtilization, bunkerHillUtilization } from '@/versions/v1/modules/ingestion/Scene.transform';
import { ProcessGraph } from '@/versions/v1/modules/ingestion/ProcessGraph';
import { RollupStrip } from '@/versions/v1/modules/ingestion/RollupStrip';
import { gmRollupFromViews } from '@/versions/v1/modules/ingestion/RollupStrip.transform';
import { SAMPLE_POINTS } from '@/versions/v1/shared/bunker-hill-seed';
import { BackToTop } from '@/versions/v1/modules/ingestion/BackToTop';
import { periodView, periodWindows } from '@/versions/v1/modules/ingestion/periodBalance';
import { BalanceStrip } from './BalanceStrip';
import {
  BALANCE_STAGES,
  balanceStripCells,
  defaultBalanceStage,
  isBalanceStage,
  STAGE_NODE,
  stageForNode,
  windowText,
  type BalanceStageId,
} from './BalanceStrip.transform';
import { StagePanel } from './StagePanel';
import { stagePanelView } from './StagePanel.transform';
import { MassBalanceLedger } from './MassBalanceLedger';
import { massBalanceLedger } from './MassBalanceLedger.transform';
import { MineToShippedCard } from './MineToShippedCard';
import { PeriodClosePanel } from './PeriodClosePanel';
import { ValueTab } from './ValueTab';
import { valueTabView } from './ValueTab.transform';
import {
  ACCOUNTING_PERIODS,
  accountingPeriodFromParam,
  MA_BAND_OMIT_METRICS,
  metalAccountingTabs,
  resolveMetalAccountingTab,
  type MetalAccountingTab,
} from './Scene.transform';

/** Metal accounting route (polish.P9 #657, locked comp
 *  `.impeccable/mocks/decision/metal-accounting.html` A): the metal's path as
 *  the strip, the plant graph with the metal-accounting layer beside the
 *  picked stage's balance, then the ledgers as pill tabs. */
export function MetalAccountingScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const project = projectTileBySlug(projectId);
  const period: AccountingPeriod = accountingPeriodFromParam(searchParams.get('period'));
  const { phase, state, diagnosis, set: setScenarioPhase, toggleScenario } = useScenario();
  const { persona } = usePersona();
  const lens = defaultRollupLensForPersona(persona);

  const ranks = useMemo(() => applyUtilization(bunkerHillUtilization(), phase, state), [phase, state]);
  const samplePoints = useMemo(() => applySamplePhase(SAMPLE_POINTS, diagnosis, state), [diagnosis, state]);
  const view = useMemo(() => periodView(period), [period]);
  const rollup = useMemo(
    () =>
      lens === 'gm'
        ? gmRollupFromViews({ ...state, whatIf: null }, ranks, periodWindows(), { omitMetrics: MA_BAND_OMIT_METRICS })
        : null,
    [lens, state, ranks],
  );

  const stageParam = searchParams.get('stage');
  const stage: BalanceStageId = isBalanceStage(stageParam) ? stageParam : defaultBalanceStage(view);
  const nodeParam = searchParams.get('node');
  const selectedNodeId = nodeParam && stageForNode(nodeParam) === stage ? nodeParam : (STAGE_NODE[stage] ?? '');

  const cells = useMemo(() => balanceStripCells(view, samplePoints), [view, samplePoints]);
  // Every stage's panel: the picked one renders, the rest size the cell (see
  // the stage stack below).
  const panels = useMemo(
    () =>
      BALANCE_STAGES.map((id) => stagePanelView(id, view, samplePoints, { withTarp: persona === 'Superintendent' })),
    [view, samplePoints, persona],
  );
  const panel = panels.find((row) => row.stage === stage) ?? panels[0]!;
  const tabs = useMemo(() => metalAccountingTabs(persona, view), [persona, view]);
  const tab = resolveMetalAccountingTab(searchParams.get('tab'), tabs);

  const updateParams = useCallback(
    (patch: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null) params.delete(key);
            else params.set(key, value);
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (!searchParams.get('period')) updateParams({ period: 'day' });
  }, [searchParams, updateParams]);

  useEffect(() => {
    document.title = project ? `Metal accounting · ${project.name}` : 'Metal accounting · OptiFlow';
  }, [project]);

  const selectStage = useCallback((next: BalanceStageId) => updateParams({ stage: next, node: null }), [updateParams]);
  const selectNode = useCallback(
    (nodeId: string) => {
      const next = stageForNode(nodeId);
      if (next) updateParams({ stage: next, node: nodeId });
    },
    [updateParams],
  );

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Plant not found" />
          <Link to="/projects" className="text-sm font-medium text-accent-700 dark:text-accent-400">
            Back to portfolio
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  // Each panel links to its own stage's node (the picked panel to the picked
  // node), so a size-only copy measures with the link its stage really has.
  const advisoryHrefFor = (row: typeof panel) => {
    const node = row.stage === stage ? selectedNodeId || row.advisoryNodeId : row.advisoryNodeId;
    return node ? `${projectSidebarHref(project.slug, 'advisory')}?scope=${node}` : null;
  };

  return (
    <SurfacePageLayout width="full">
      <Commentable name="MetalAccountingScene">
        <SurfacePageBody className="space-y-3" width="full">
          <div id="metal-accounting-page-header" tabIndex={-1} className="outline-none">
            <SurfacePageHeader
              title="Metal accounting"
              titleSize="large"
              actions={
                <>
                  <SurfacePageTabs
                    aria-label="Accounting period"
                    variant="ink"
                    value={period}
                    onChange={(next) => updateParams({ period: next })}
                    items={ACCOUNTING_PERIODS}
                    className="gap-0.5 rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-1)] p-[3px] [&>button]:px-2.5 [&>button]:py-1 [&>button]:text-xs"
                  />
                  <AskAssistant threadId="thread-ma-pb-reconciliation" label="Ask about Pb reconciliation" />
                  <HelpAnchor doc="metal-accounting/overview" label="Metal accounting" />
                </>
              }
            />
          </div>

          {rollup ? <RollupStrip view={rollup} /> : null}

          <BalanceStrip cells={cells} selected={stage} onSelect={selectStage} />

          {/* Flowsheet beside the stage panel from 1400 px, as the comp draws
              it at 1470. The stage column is 320 px so the flowsheet pane holds
              the locked layout at zoom 1 (text at its 10 px floor, never
              below). Narrower, the stage panel moves under the graph. The
              in-circuit figure is not repeated in the header: the Closure
              cell says it. */}
          <div className="grid min-w-0 gap-3 min-[1400px]:grid-cols-[minmax(0,1fr)_320px] min-[1400px]:items-stretch">
            <SurfacePagePanel
              title="Metal on the flowsheet"
              contentClassName="flex flex-col px-3 pb-2 pt-1"
            >
              <ProcessGraph
                ranks={ranks}
                phase={phase}
                state={state}
                onPhaseChange={setScenarioPhase}
                onToggleScenario={toggleScenario}
                samplePoints={samplePoints}
                projectSlug={project.slug}
                lens={lens}
                selectedNodeId={selectedNodeId}
                onSelectNode={selectNode}
                maLocked
                hideMaToggle
                hideMaStripLink
                hideMaStripChainGapCell
                hideMaStrip
                hideRollup
                bare
                legendAside={windowText(view)}
                period={period}
                view={view}
              />
            </SurfacePagePanel>
            {/* Every stage's panel shares one grid cell: the picked one shows,
                the others hold the cell at the tallest panel's height, so the
                row — and the flowsheet card and the tab row under it — keeps
                its height when the stage changes. */}
            <div className="grid min-w-0 [grid-template-areas:'stage']" data-stage-stack>
              {panels.map((row) => (
                <StagePanel
                  key={row.stage}
                  panel={row}
                  advisoryHref={advisoryHrefFor(row)}
                  ghost={row.stage !== stage}
                  className="[grid-area:stage]"
                />
              ))}
            </div>
          </div>

          <SurfacePageTabs
            aria-label="Ledgers"
            variant="ink"
            items={tabs}
            value={tab}
            onChange={(next: MetalAccountingTab) => updateParams({ tab: next })}
            className="pt-3"
          />
          <div role="tabpanel" aria-label={tabs.find((item) => item.id === tab)?.label} data-ledger-tab={tab} className="card-matte-2 min-w-0 px-4 py-3">
            {tab === 'value' ? <ValueTab value={valueTabView(view)} /> : null}
            {tab === 'ledger' ? <MassBalanceLedger rows={massBalanceLedger(view, samplePoints)} /> : null}
            {tab === 'chain' ? <MineToShippedCard view={view} /> : null}
            {tab === 'close' ? <PeriodClosePanel view={view} /> : null}
          </div>

          <BackToTop headerId="metal-accounting-page-header" />
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
